import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/UI';
import styles from './AdminDashboard.module.css';
import {
  Users, Search, LogOut, Trash2, Globe,
  Briefcase, Clock, Filter, Eye, Download, FileSpreadsheet
} from 'lucide-react';
import {
  FIELD_EPI_SKILLS, PROJECT_MGMT_SKILLS, ME_SKILLS,
  GHS_SKILLS, INCIDENT_SKILLS, LEADERSHIP_SKILLS,
} from '../data/formSchema';

const RATING_LABELS = ['', 'Basic', 'Limited', 'Proficient', 'Highly Proficient', 'Expert'];

const SECTOR_SHORT = {
  'Government (Ministry or Public Health Institute)': 'Government',
  'International NGO (INGO)': "Int'l NGO",
  'Local NGO': 'Local NGO',
  'Academic/Research Institution': 'Academic',
  'United Nations Agency (e.g., WHO, UNICEF)': 'UN Agency',
  'African Union Agency (e.g., Africa CDC, AU IBAR, AU NEPAD)': 'AU Agency',
  'Regional Economic Community (e.g., IGAD, EAC, COMESA)': 'REC',
  'Private Sector': 'Private',
  'Consultant (Self-employed)': 'Consultant',
  Other: 'Other',
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [filterAvail, setFilterAvail]   = useState('');
  const [sortBy, setSortBy]             = useState('newest');
  const [deleting, setDeleting]         = useState(null);

  const fetchData = async () => {
    setLoading(true);
    const q    = query(collection(db, 'alumni'), orderBy('submittedAt', 'desc'));
    const snap = await getDocs(q);
    setMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleDownloadLocalReady = async () => {
    const IMMEDIATE = 'Immediately (within 24–48 hours)';
    const ONE_WEEK  = '1 week';

    const ready = members.filter(m =>
      m.localDeployment === 'Yes' &&
      (m.noticePeriod === IMMEDIATE || m.noticePeriod === ONE_WEEK)
    );

    if (ready.length === 0) {
      alert('No members currently meet this criteria.');
      return;
    }

    const formatNotice = (n) => n === IMMEDIATE ? 'Immediate' : '1 Week';

    // Dynamically load jsPDF + autoTable from CDN
    const loadScript = (src) => new Promise((res, rej) => {
      if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
      const s = document.createElement('script');
      s.src = src; s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });

    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js');

    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 18;

    // ── Attempt to embed logo ──
    let logoLoaded = false;
    try {
      const resp = await fetch('/logo.png');
      if (resp.ok) {
        const blob = await resp.blob();
        const dataUrl = await new Promise((res) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.readAsDataURL(blob);
        });
        // Logo top-left
        doc.addImage(dataUrl, 'PNG', margin, 12, 22, 22);
        logoLoaded = true;
      }
    } catch (_) { /* logo unavailable — skip */ }

    // ── Header band ──
    doc.setFillColor(15, 52, 96);       // dark navy
    doc.rect(0, 0, pageW, 40, 'F');

    // Re-draw logo on top of band if loaded
    if (logoLoaded) {
      try {
        const resp2 = await fetch('/logo.png');
        const blob2 = await resp2.blob();
        const dataUrl2 = await new Promise((res) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.readAsDataURL(blob2);
        });
        doc.addImage(dataUrl2, 'PNG', margin, 9, 22, 22);
      } catch (_) {}
    }

    // Organisation name
    const textX = logoLoaded ? margin + 27 : margin;
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('Field Epidemiologists without Borders', textX, 24);

    // ── Divider line below header ──
    doc.setDrawColor(200, 160, 60);   // gold accent
    doc.setLineWidth(0.8);
    doc.line(0, 40, pageW, 40);

    // ── Table ──
    doc.autoTable({
      startY: 50,
      margin: { left: margin, right: margin },
      head: [['#', 'Full Name', 'Email Address', 'Phone Number', 'Notice Period']],
      body: ready.map((m, i) => [
        i + 1,
        `${m.firstName || ''} ${m.surname || ''}`.trim(),
        m.preferredEmail || '—',
        m.primaryPhone   || '—',
        formatNotice(m.noticePeriod),
      ]),
      headStyles: {
        fillColor: [15, 52, 96],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [240, 245, 252] },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 42 },
        2: { cellWidth: 60 },
        3: { cellWidth: 36 },
        4: { cellWidth: 26, halign: 'center' },
      },
      tableLineColor: [200, 200, 200],
      tableLineWidth: 0.1,
      didDrawPage: (data) => {
        // Footer on every page
        const pgNum = doc.internal.getCurrentPageInfo().pageNumber;
        const total = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.setFont('helvetica', 'normal');
        doc.text(
          'Field Epidemiologists without Borders',
          margin, pageH - 8
        );
        doc.text(
          `Page ${pgNum} of ${total}`,
          pageW - margin, pageH - 8,
          { align: 'right' }
        );
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
      },
    });

    doc.save('FEwB_Local_Deployment_Ready_Members.pdf');
  };

  const handleDownloadAllExcel = async () => {
    if (members.length === 0) {
      alert('No submissions available to download yet.');
      return;
    }

    // Dynamically load SheetJS (xlsx) from CDN
    const loadScript = (src) => new Promise((res, rej) => {
      if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
      const s = document.createElement('script');
      s.src = src; s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });

    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js');
    const XLSX = window.XLSX;

    const skillCell = (data, key) => {
      const val = data?.[key] || 0;
      return val ? `${val} - ${RATING_LABELS[val]}` : '';
    };

    const yesNo = (v) => (v ? 'Yes' : 'No');

    // Determine how many reference slots to include (structured or legacy)
    const maxRefs = Math.max(
      3,
      ...members.map(m => (m.references || []).length)
    );

    const header = [
      // Section A — Personal & Contact
      'Title', 'First Name', 'Surname', 'Nationality', 'Country of Residence',
      'Preferred Email', 'Alternative Email', 'Primary Phone', 'Secondary Phone',
      'Language - English', 'Language - Arabic', 'Language - French', 'Language - Kiswahili',
      'Language - Portuguese', 'Language - Chinese', 'Language - Russian', 'Language - Spanish',
      'Other Language (name)', 'Other Language (level)',
      // Section B — Professional Background
      'Highest Qualification', 'Year of FETP Graduation', 'Years of Experience',
      'Current Employer', 'Current Role Description', 'Sector', 'Sector (Other)',
      // Section C — Availability
      'Available for Local Deployment', 'Available for International Deployment',
      'Notice Period Required', 'Notice Period (Other, specified)',
      'Preferred Duration - Short-term (up to 2 weeks)',
      'Preferred Duration - Medium-term (2 weeks to 2 months)',
      'Preferred Duration - Long-term (2-6 months)',
      'Preferred Duration - Extended (6+ months)',
      'Deployment Restrictions / Notes',
      // Section D — Skills
      ...FIELD_EPI_SKILLS.map(s => s.label),
      'Field Epidemiology - Other Skills', 'Other Analysis Software (name)',
      ...PROJECT_MGMT_SKILLS.map(s => s.label),
      'Project Management - Other Skills',
      ...ME_SKILLS.map(s => s.label),
      'Monitoring & Evaluation - Other Skills',
      ...GHS_SKILLS.map(s => s.label),
      'Global Health Security - Other Skills',
      ...INCIDENT_SKILLS.map(s => s.label),
      'Incident Management - Other Skills',
      ...LEADERSHIP_SKILLS.map(s => s.label),
      'Leadership & Training - Other Skills',
      // Section E — Specific Expertise
      'Diseases/Pathogens - Specific Expertise & Experience',
      ...Array.from({ length: 5 }, (_, i) => `Country of Work Experience ${i + 1}`),
      'Continents of Work Experience',
      'Humanitarian / Fragile Settings Experience',
      'Humanitarian Context',
      'Willing to Work in High-Risk Environments',
      'Technical Certifications & Advanced Training',
      'Additional Skills & Areas of Expertise',
      // Section F — References
      ...Array.from({ length: maxRefs }, (_, i) => [
        `Reference ${i + 1} - Name`,
        `Reference ${i + 1} - Title`,
        `Reference ${i + 1} - Organization`,
        `Reference ${i + 1} - Email`,
        `Reference ${i + 1} - Phone`,
      ]).flat(),
      // Meta
      'Consent Given', 'Time Taken to Complete Form', 'Date Submitted', 'Record ID',
    ];

    const rows = members.map(m => {
      const refs = (m.references || []);
      const refCells = Array.from({ length: maxRefs }, (_, i) => {
        const r = refs[i];
        if (r) return [r.name || '', r.title || '', r.organization || '', r.email || '', r.phone || ''];
        // fall back to legacy plain-text reference fields (ref1/ref2/ref3)
        const legacy = i === 0 ? m.ref1 : i === 1 ? m.ref2 : i === 2 ? m.ref3 : '';
        return legacy ? [legacy, '', '', '', ''] : ['', '', '', '', ''];
      }).flat();

      const submitted = m.submittedAt?.toDate
        ? m.submittedAt.toDate().toLocaleString('en-GB')
        : '';

      return [
        m.title === 'Other' ? m.titleOther : (m.title || ''),
        m.firstName || '', m.surname || '', m.nationality || '', m.currentCountry || '',
        m.preferredEmail || '', m.alternativeEmail || '', m.primaryPhone || '', m.secondaryPhone || '',
        m.languages?.english || '', m.languages?.arabic || '', m.languages?.french || '',
        m.languages?.kiswahili || m.languages?.swahili || '', m.languages?.portuguese || '',
        m.languages?.chinese || '', m.languages?.russian || '', m.languages?.spanish || '',
        m.languages?.otherLanguage || '', m.languages?.other || '',
        m.highestQualification || '', m.graduationYear || '', m.yearsExperience || '',
        m.currentEmployer || '', m.currentRole || '', m.sector || '', m.sectorOther || '',
        m.localDeployment || '', m.internationalDeployment || '',
        m.noticePeriod || '', m.noticePeriodOther || '',
        yesNo(m.deploymentDuration?.shortTerm), yesNo(m.deploymentDuration?.mediumTerm),
        yesNo(m.deploymentDuration?.longTerm), yesNo(m.deploymentDuration?.extended),
        m.deploymentRestrictions || '',
        ...FIELD_EPI_SKILLS.map(s => skillCell(m.fieldEpiSkills, s.key)),
        m.fieldEpiOtherSkills || '', m.otherSoftwareName || '',
        ...PROJECT_MGMT_SKILLS.map(s => skillCell(m.projectMgmtSkills, s.key)),
        m.projectMgmtOtherSkills || '',
        ...ME_SKILLS.map(s => skillCell(m.meSkills, s.key)),
        m.meOtherSkills || '',
        ...GHS_SKILLS.map(s => skillCell(m.ghsSkills, s.key)),
        m.ghsOtherSkills || '',
        ...INCIDENT_SKILLS.map(s => skillCell(m.incidentSkills, s.key)),
        m.incidentOtherSkills || '',
        ...LEADERSHIP_SKILLS.map(s => skillCell(m.leadershipSkills, s.key)),
        m.leadershipOtherSkills || '',
        m.diseasesExperience || '',
        ...Array.from({ length: 5 }, (_, i) => m.workRegions?.[i] || ''),
        (m.workContinents || []).join(', '),
        m.humanitarianExperience || '', m.humanitarianContext || '',
        m.highRiskWillingness || '',
        m.certifications || '', m.additionalSkills || '',
        ...refCells,
        m.consent ? 'Yes' : 'No', m.formDurationDisplay || '', submitted, m.id || '',
      ];
    });

    const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
    worksheet['!cols'] = header.map(() => ({ wch: 22 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Alumni Questionnaire Data');

    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `FEwB_Alumni_Full_Data_${stamp}.xlsx`);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this member record? This cannot be undone.')) return;
    setDeleting(id);
    await deleteDoc(doc(db, 'alumni', id));
    setMembers(prev => prev.filter(m => m.id !== id));
    setDeleting(null);
  };

  const sectors = useMemo(
    () => [...new Set(members.map(m => m.sector).filter(Boolean))],
    [members]
  );

  const filtered = useMemo(() => {
    let list = [...members];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        `${m.firstName} ${m.surname}`.toLowerCase().includes(q) ||
        m.preferredEmail?.toLowerCase().includes(q) ||
        m.currentCountry?.toLowerCase().includes(q) ||
        m.currentEmployer?.toLowerCase().includes(q) ||
        m.graduationYear?.toString().toLowerCase().includes(q)
      );
    }
    if (filterSector) list = list.filter(m => m.sector === filterSector);
    if (filterAvail)
      list = list.filter(
        m => m.internationalDeployment === filterAvail || m.localDeployment === filterAvail
      );
    if (sortBy === 'name')
      list.sort((a, b) =>
        `${a.firstName}${a.surname}`.localeCompare(`${b.firstName}${b.surname}`)
      );
    return list;
  }, [members, search, filterSector, filterAvail, sortBy]);

  const availableCount = members.filter(m => m.internationalDeployment === 'Yes').length;
  const countriesCount = new Set(members.map(m => m.currentCountry).filter(Boolean)).size;
  const avgYears = members.length
    ? Math.round(members.reduce((s, m) => s + (parseInt(m.yearsExperience) || 0), 0) / members.length)
    : 0;

  return (
    <div className={styles.page}>
      {/* ── Sidebar ── */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarTop}>
          <div className={styles.sidebarLogo}>
            <img src="/logo.png" alt="FEwB" className={styles.sidebarLogoImg} />
            <div className={styles.logoLabel}>Admin</div>
          </div>
          <nav className={styles.nav}>
            <div className={`${styles.navItem} ${styles.navItemActive}`}>
              <Users size={16} /> <span>Members</span>
            </div>
          </nav>
        </div>
        <div className={styles.sidebarBottom}>
          <div className={styles.userInfo}>
            <div className={styles.userAvatar}>{user?.email?.[0]?.toUpperCase()}</div>
            <div className={styles.userEmail}>{user?.email}</div>
          </div>
          <button className={styles.logoutBtn} onClick={logout}>
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className={styles.main}>
        <header className={styles.topbar}>
          <div>
            <h1 className={styles.pageTitle}>Alumni Roster</h1>
            <p className={styles.pageSubtitle}>Field Epidemiologists without Borders — Member Profiles</p>
          </div>
          <div className={styles.topbarRight}>
            <button className={styles.downloadBtn} onClick={handleDownloadAllExcel} title="Download all questionnaire information for every member as an Excel file">
              <FileSpreadsheet size={14} /> Download All Data (Excel)
            </button>
            <button className={styles.downloadBtn} onClick={handleDownloadLocalReady} title="Download members available for local deployment with immediate or 1-week notice">
              <Download size={14} /> Download Local-Ready Members
            </button>
            <Badge color="teal">{members.length} Total Members</Badge>
          </div>
        </header>

        {/* Stats */}
        <div className={styles.stats}>
          <StatCard icon={<Users size={20} />}     label="Total Members"   value={members.length} color="teal"  />
          <StatCard icon={<Globe size={20} />}     label="Countries"       value={countriesCount} color="sand"  />
          <StatCard icon={<Clock size={20} />}     label="Available Intl." value={availableCount} color="green" />
          <StatCard icon={<Briefcase size={20} />} label="Avg. Exp. (yrs)" value={avgYears}       color="gray"  />
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          <div className={styles.searchWrap}>
            <Search size={15} className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Search by name, email, country, employer, graduation year…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className={styles.filterRow}>
            <Filter size={14} style={{ color: 'var(--gray-400)' }} />
            <select className={styles.filterSelect} value={filterSector} onChange={e => setFilterSector(e.target.value)}>
              <option value="">All Sectors</option>
              {sectors.map(s => <option key={s} value={s}>{SECTOR_SHORT[s] || s}</option>)}
            </select>
            <select className={styles.filterSelect} value={filterAvail} onChange={e => setFilterAvail(e.target.value)}>
              <option value="">All Availability</option>
              <option value="Yes">Available</option>
              <option value="No">Not Available</option>
              <option value="Depends on circumstances">Depends</option>
            </select>
            <select className={styles.filterSelect} value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="newest">Newest First</option>
              <option value="name">Name A–Z</option>
            </select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner} />
            <p>Loading member data…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>
            <Users size={40} style={{ color: 'var(--gray-300)' }} />
            <p>{members.length === 0 ? 'No submissions yet.' : 'No results match your filters.'}</p>
          </div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Email</th>
                  <th>Country</th>
                  <th>Qualification</th>
                  <th>Sector</th>
                  <th>Exp.</th>
                  <th>Intl. Avail.</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(m => (
                  <tr key={m.id} className={styles.row}>
                    <td>
                      <div className={styles.nameCell}>
                        <div className={styles.avatar}>{m.firstName?.[0]}{m.surname?.[0]}</div>
                        <span className={styles.memberName}>{m.firstName} {m.surname}</span>
                      </div>
                    </td>
                    <td className={styles.mono}>{m.preferredEmail || '—'}</td>
                    <td>{m.currentCountry || '—'}</td>
                    <td>
                      {m.highestQualification
                        ? <Badge color="sand">{m.highestQualification}</Badge>
                        : '—'}
                    </td>
                    <td>{SECTOR_SHORT[m.sector] || m.sector || '—'}</td>
                    <td className={styles.center}>{m.yearsExperience ? `${m.yearsExperience} yrs` : '—'}</td>
                    <td className={styles.center}><AvailBadge value={m.internationalDeployment} /></td>
                    <td className={styles.date}>
                      {m.submittedAt?.toDate
                        ? m.submittedAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.viewBtn}
                          onClick={() => navigate(`/admin/member/${m.id}`)}
                          title="View full profile"
                        >
                          <Eye size={14} />
                          View
                        </button>
                        <button
                          className={styles.deleteBtn}
                          onClick={e => handleDelete(m.id, e)}
                          disabled={deleting === m.id}
                          title="Delete record"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

/* ────────────────────────────────────────────
   Sub-components
──────────────────────────────────────────── */

function StatCard({ icon, label, value, color }) {
  const colors = {
    teal:  { bg: 'var(--navy-50)',    accent: 'var(--navy-500)',    text: 'var(--navy-800)'    },
    sand:  { bg: 'var(--warn-bg)',    accent: '#d97706',            text: 'var(--warn-text)'   },
    green: { bg: 'var(--success-bg)', accent: 'var(--success-text)', text: 'var(--success-text)' },
    gray:  { bg: 'var(--gray-100)',   accent: 'var(--gray-500)',    text: 'var(--gray-700)'    },
  };
  const c = colors[color] || colors.gray;
  return (
    <div className={styles.statCard} style={{ background: c.bg }}>
      <div className={styles.statIcon}  style={{ color: c.accent }}>{icon}</div>
      <div className={styles.statValue} style={{ color: c.text }}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

function AvailBadge({ value }) {
  if (value === 'Yes') return <Badge color="green">Yes</Badge>;
  if (value === 'No')  return <Badge color="red">No</Badge>;
  if (value)           return <Badge color="sand">Depends</Badge>;
  return <span style={{ color: 'var(--gray-300)' }}>—</span>;
}